/**
 * Search-text helpers shared by the party search inputs (#3641).
 *
 * The work-order wizard's unified search and the reusable PartyPicker both
 * offer "create new person/company" rows built from what the user typed. These
 * helpers classify that text and turn it into a create-form prefill so both
 * places fill the same fields the same way.
 */

import type { PersonFieldsData } from '@/components/persons/PersonFormFields';
import type { CompanyFieldsData } from '@/components/companies/CompanyFormFields';

/** Finnish business id: 1234567-8. */
export function isBusinessIdLike(input: string): boolean {
  return /^\d{7}-\d$/.test(input.trim());
}

/** Company-name detector: legal-form suffix on the last word, or a business id. */
export function looksLikeCompany(input: string): boolean {
  if (isBusinessIdLike(input)) return true;
  const words = input.trim().split(/\s+/);
  const last = words[words.length - 1] ?? '';
  return /^(oy|oyj|ab|ky|tmi|ry|osk|oü|gmbh|ltd|inc|ag)\.?$/i.test(last);
}

/** Split typed text into first/last name for a person prefill. */
export function parsePersonName(input: string): { firstName: string; lastName: string } {
  const words = input.trim().split(/\s+/).filter(Boolean);
  return {
    firstName: words[0] ?? '',
    lastName: words.slice(1).join(' '),
  };
}

/** Loose email detector: something@something.tld, no spaces. */
export function isEmailLike(input: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.trim());
}

/**
 * Loose phone detector: only digits, spaces, dashes, parentheses and an
 * optional leading +, with at least five digits ("040 123 4567", "+358 40…").
 */
export function isPhoneLike(input: string): boolean {
  const trimmed = input.trim();
  if (!/^\+?[\d\s\-()]+$/.test(trimmed)) return false;
  return trimmed.replace(/\D/g, '').length >= 5;
}

/**
 * Person create-form prefill from search text: an email or phone goes to that
 * field, anything else is split into first/last name.
 */
export function personPrefillFromSearch(input: string): Partial<PersonFieldsData> {
  const trimmed = input.trim();
  if (!trimmed) return {};
  // A person has no business id field; a Y-tunnus is not a phone number.
  if (isBusinessIdLike(trimmed)) return {};
  if (isEmailLike(trimmed)) return { email: trimmed };
  if (isPhoneLike(trimmed)) return { phone: trimmed };
  return parsePersonName(trimmed);
}

/**
 * Company create-form prefill from search text: a business id, email or phone
 * goes to that field, anything else is the company name.
 */
export function companyPrefillFromSearch(input: string): Partial<CompanyFieldsData> {
  const trimmed = input.trim();
  if (!trimmed) return {};
  if (isBusinessIdLike(trimmed)) return { businessId: trimmed };
  if (isEmailLike(trimmed)) return { email: trimmed };
  if (isPhoneLike(trimmed)) return { phone: trimmed };
  return { name: trimmed };
}
